import { getDBAsync } from '@/db';
import type { Match, MatchWithGames, MatchStatus, Game } from '@/types';
import { generateId } from '@/lib/utils';
import { MATCH_FORMAT_CONFIG } from '@/types';
import { gameRepo } from './gameRepo';
import { roundRepo } from './roundRepo';
import { calculateTotals } from '@/lib/scoring';

export const matchRepo = {
  /**
   * Get all matches
   */
  async getAll(): Promise<Match[]> {
    const db = await getDBAsync();
    return db.getAll('matches');
  },

  /**
   * Get a match by ID
   */
  async getById(id: string): Promise<Match | undefined> {
    const db = await getDBAsync();
    return db.get('matches', id);
  },

  /**
   * Get a match with all games and calculated wins
   */
  async getWithGames(id: string): Promise<MatchWithGames | null> {
    const match = await this.getById(id);
    if (!match) {
      return null;
    }

    const games = await gameRepo.getByMatchId(id);
    const gameWins = this.calculateGameWins(games, match.participants);
    const gamesNeededToWin = MATCH_FORMAT_CONFIG[match.format].gamesToWin;

    // Find and load current game with totals (active game, or most recent completed if no active)
    let currentGame = undefined;
    const activeGame = games.find((g) => g.status === 'active');
    if (activeGame) {
      const rounds = await roundRepo.getByGameId(activeGame.id);
      const totals = calculateTotals(activeGame.participants, rounds);
      currentGame = { ...activeGame, rounds, totals };
    } else if (games.length > 0) {
      // No active game - load rounds for the most recent game (highest gameNumber)
      const mostRecentGame = games.reduce((latest, game) =>
        game.gameNumber > latest.gameNumber ? game : latest
      );
      const rounds = await roundRepo.getByGameId(mostRecentGame.id);
      const totals = calculateTotals(mostRecentGame.participants, rounds);
      currentGame = { ...mostRecentGame, rounds, totals };
    }

    return {
      ...match,
      games,
      gameWins,
      gamesNeededToWin,
      currentGame,
    };
  },

  /**
   * Calculate game wins for each participant
   */
  calculateGameWins(
    games: Game[],
    participants: Match['participants']
  ): Record<string, number> {
    const wins: Record<string, number> = {};

    // Initialize all participants with 0 wins
    for (const p of participants) {
      wins[p.id] = 0;
    }

    // Count wins from completed games
    for (const game of games) {
      if (game.status === 'completed' && game.winnerId) {
        wins[game.winnerId] = (wins[game.winnerId] || 0) + 1;
      }
    }

    return wins;
  },

  /**
   * Get matches by status
   */
  async getByStatus(status: MatchStatus): Promise<Match[]> {
    const db = await getDBAsync();
    return db.getAllFromIndex('matches', 'by-status', status);
  },

  /**
   * Get active matches
   */
  async getActive(): Promise<Match[]> {
    return this.getByStatus('active');
  },

  /**
   * Get completed matches (sorted by completion date, newest first)
   */
  async getCompleted(): Promise<Match[]> {
    const matches = await this.getByStatus('completed');
    return matches.sort(
      (a, b) =>
        (b.completedAt?.getTime() ?? 0) - (a.completedAt?.getTime() ?? 0)
    );
  },

  /**
   * Create a new match
   */
  async create(
    config: Pick<Match, 'format' | 'mode' | 'targetScore' | 'participants'>
  ): Promise<Match> {
    const db = await getDBAsync();
    const match: Match = {
      id: generateId(),
      format: config.format,
      mode: config.mode,
      targetScore: config.targetScore,
      status: 'active',
      participants: config.participants,
      createdAt: new Date(),
    };
    await db.put('matches', match);
    return match;
  },

  /**
   * Update a match
   */
  async update(id: string, updates: Partial<Match>): Promise<Match | null> {
    const db = await getDBAsync();
    const existing = await db.get('matches', id);
    if (!existing) {
      return null;
    }

    const updated: Match = {
      ...existing,
      ...updates,
    };
    await db.put('matches', updated);
    return updated;
  },

  /**
   * Complete a match with a winner
   */
  async complete(id: string, winnerId: string): Promise<Match | null> {
    return this.update(id, {
      status: 'completed',
      winnerId,
      completedAt: new Date(),
    });
  },

  /**
   * Delete a match and all its games/rounds
   */
  async delete(id: string): Promise<boolean> {
    const db = await getDBAsync();
    const existing = await db.get('matches', id);
    if (!existing) {
      return false;
    }

    // Delete all games for this match (which also deletes their rounds)
    const games = await gameRepo.getByMatchId(id);
    for (const game of games) {
      await gameRepo.delete(game.id);
    }

    // Delete the match
    await db.delete('matches', id);
    return true;
  },

  /**
   * Get recent matches (both active and completed)
   */
  async getRecent(limit: number = 10): Promise<Match[]> {
    const db = await getDBAsync();
    const all = await db.getAll('matches');
    return all
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
      .slice(0, limit);
  },

  /**
   * Get match count by status
   */
  async countByStatus(status: MatchStatus): Promise<number> {
    const matches = await this.getByStatus(status);
    return matches.length;
  },

  /**
   * Get all matches with their games (for statistics)
   */
  async getAllWithGames(): Promise<MatchWithGames[]> {
    const matches = await this.getAll();
    const result: MatchWithGames[] = [];

    for (const match of matches) {
      const matchWithGames = await this.getWithGames(match.id);
      if (matchWithGames) {
        result.push(matchWithGames);
      }
    }

    return result;
  },
};
